import type { CanFrame } from '../canlog/types';

const VIN_LENGTH = 17;

export type VinBroadcastCandidate = {
  canId: number;
  canIdHex: string;
  sequenceCount: number;
  firstSeenMs: number;
  lastSeenMs: number;
  redactedVin: string;
  preview: string;
};

function isPadding(byte: number) {
  return byte === 0x00 || byte === 0xff;
}

function isPrintableAscii(byte: number) {
  return byte >= 32 && byte <= 126;
}

function isVinCharacter(value: string) {
  return /^[A-HJ-NPR-Z0-9]$/.test(value);
}

function normalizeVinBytes(frames: CanFrame[]) {
  const bytes: number[] = [];
  for (const frame of frames) {
    for (let index = 1; index < Math.min(frame.dlc, 8); index += 1) {
      const byte = frame.data[index];
      if (isPadding(byte)) continue;
      if (!isPrintableAscii(byte)) return null;
      bytes.push(byte);
    }
  }

  const text = String.fromCharCode(...bytes).trim();
  const vin = text.slice(0, VIN_LENGTH).toUpperCase();
  if (vin.length !== VIN_LENGTH) return null;
  if (![...vin].every(isVinCharacter)) return null;
  return vin;
}

function redactVin(vin: string) {
  return `${vin.slice(0, 3)}${'•'.repeat(11)}${vin.slice(-3)}`;
}

function previewVin(vin: string) {
  return `${vin.slice(0, 3)} ${'•••• •••• •••'.trim()} ${vin.slice(-3)}`;
}

export function detectVinBroadcasts(frames: CanFrame[]): VinBroadcastCandidate[] {
  const byCanId = new Map<number, CanFrame[]>();
  for (const frame of frames) {
    if (frame.dlc < 2) continue;
    const sequenceByte = frame.data[0];
    if (sequenceByte !== 0 && sequenceByte !== 1 && sequenceByte !== 2) continue;
    if (!byCanId.has(frame.canId)) byCanId.set(frame.canId, []);
    byCanId.get(frame.canId)?.push(frame);
  }

  const candidates = new Map<string, VinBroadcastCandidate>();

  for (const framesForId of byCanId.values()) {
    const ordered = [...framesForId].sort((a, b) => a.timestampMs - b.timestampMs || a.index - b.index);
    for (let index = 0; index <= ordered.length - 3; index += 1) {
      const sequence = ordered.slice(index, index + 3);
      if (sequence[0].data[0] !== 0 || sequence[1].data[0] !== 1 || sequence[2].data[0] !== 2) continue;

      const vin = normalizeVinBytes(sequence);
      if (!vin) continue;

      const key = `${sequence[0].canId}:${vin}`;
      const existing = candidates.get(key);
      if (existing) {
        existing.sequenceCount += 1;
        existing.lastSeenMs = sequence[2].timestampMs;
      } else {
        candidates.set(key, {
          canId: sequence[0].canId,
          canIdHex: sequence[0].canIdHex,
          sequenceCount: 1,
          firstSeenMs: sequence[0].timestampMs,
          lastSeenMs: sequence[2].timestampMs,
          redactedVin: redactVin(vin),
          preview: previewVin(vin)
        });
      }
    }
  }

  return [...candidates.values()].sort((a, b) => b.sequenceCount - a.sequenceCount);
}
